import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, SubmitButton } from '../../../components/mui/Buttons/Buttons';
import DatePickerCustom from '../../../components/DatePicker/DatePickerCustom';
import BandLadderBuilder from './BandLadderBuilder';
import {
  CHALLENGE_SCOPES,
  CHALLENGE_TYPES,
  MAX_PARTITION_DAYS,
  PARTITION_TYPES,
  daysBetween,
  roundLength,
  getAssignmentOptions,
  getAssignmentPeople,
  getChallengeById,
} from '../../../utils/incentiveChallengeApi';
import {
  createChallenge,
  getChallenges,
  updateChallenge,
} from '../../../redux/features/challengeSlice';

/** Mirrors the server's validation so the common mistakes are caught locally. */
/**
 * Returns null, or { message, field } naming the input at fault so it can be
 * outlined in red. A message on its own leaves Finance hunting for which of
 * nine boxes it means, and a prize ladder can have dozens.
 */
const fault = (field, message) => ({ field, message });

function validateForm(values) {
  if (!values.name?.trim()) return fault('name', 'Name is required');
  if (!values.type) return fault('type', 'Type is required');
  if (!values.scopeDeptId) return fault('scopeDeptId', 'Scope is required');
  if (!values.assignmentRef) return fault('assignmentRef', 'Assignment is required');
  if (!values.startDate) return fault('startDate', 'Start date is required');
  if (!values.endDate) return fault('endDate', 'End date is required');
  if (values.endDate < values.startDate) {
    return fault('endDate', 'End date cannot be before the start date');
  }
  if (values.partitionType === 'every_n') {
    const n = Number(values.partitionDays);
    if (!Number.isInteger(n) || n < 2 || n > MAX_PARTITION_DAYS) {
      return fault('partitionDays', `Every N days needs N between 2 and ${MAX_PARTITION_DAYS}`);
    }
  }

  // Every challenge is its ladder — one row or several — so there is a single
  // shape to check.
  return validateLadder(values);
}

/**
 * The ladder, checked here as well as in the API.
 *
 * Overlapping groups are the one that matters: 15 OGD people carry a revenue
 * target of exactly ₹2,50,000, and two groups both containing that figure would
 * pay them twice. Catching it here means Finance is told before losing the form.
 */
function validateLadder(values) {
  const groups = values.bands || [];
  if (!groups.length) return fault('bands', 'Add at least one target group');

  const isCount = values.type === 'admission';
  const seen = [];

  for (const [i, group] of groups.entries()) {
    const name = `Target Group ${i + 1}`;
    const blank = (v) => v === '' || v === null || v === undefined;
    const from = blank(group.from) ? null : Number(group.from);
    const to = blank(group.to) ? null : Number(group.to);

    if (from !== null && to !== null && to < from) {
      return fault(`band.${i}.to`, `${name}: the upper figure cannot be below the lower one`);
    }
    for (const other of seen) {
      const apart =
        (to !== null && other.from !== null && to < other.from) ||
        (from !== null && other.to !== null && from > other.to);
      if (!apart) {
        return fault(
          `band.${i}.from`,
          `${name} overlaps ${other.name} — a person would be in both`,
        );
      }
    }
    seen.push({ from, to, name });

    const rungs = group.tiers || [];
    if (!rungs.length) return fault(`band.${i}`, `${name}: add at least one target and prize`);

    let previous = 0;
    for (const [j, rung] of rungs.entries()) {
      const at = `band.${i}.tier.${j}`;
      const value = Number(rung.target);
      if (!Number.isFinite(value) || value <= 0) {
        return fault(`${at}.target`, `${name}, row ${j + 1}: target must be greater than zero`);
      }
      if (isCount && !Number.isInteger(value)) {
        return fault(
          `${at}.target`,
          `${name}, row ${j + 1}: an admission target must be a whole number`,
        );
      }
      if (value <= previous) {
        return fault(`${at}.target`, `${name}, row ${j + 1}: must be higher than the one above it`);
      }
      previous = value;
      if (!rung.prize?.trim()) {
        return fault(`${at}.prize`, `${name}, row ${j + 1}: prize is required`);
      }
    }
  }
  return null;
}

/** Roles scored on their team rather than on themselves. Mirrors the API. */
const HEAD_ROLES = ['PM', 'CRM', 'SOGO', 'OGM'];

/** Only OGD carries the monthly revenue targets a prize ladder bands on. */
const OGD_DEPT_ID = 11;

const EMPTY = {
  name: '',
  type: '',
  scopeDeptId: '',
  assignmentType: '',
  assignmentRef: '',
  startDate: '',
  endDate: '',
  participantUserIds: [],
  // Display only, never sent: a pooled assignment can be scored per person, and
  // when it is, it is SAVED as the individual role narrowed to those people.
  scoredIndividually: false,
  bands: [{ from: '', to: '', tiers: [{ target: '', prize: '' }] }],
  partitionType: 'none',
  partitionDays: '',
};

/** Today in IST, so a round closing at midnight closes at Indian midnight. */
const todayIst = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

/** Last day of a challenge's first round — the whole challenge when it has none. */
const firstRoundEnd = (c) => {
  const size = roundLength(c.partitionType, c.partitionDays);
  if (!size) return c.endDate;
  const end = dayjs(c.startDate).add(size - 1, 'day').format('YYYY-MM-DD');
  return end < c.endDate ? end : c.endDate;
};

/** The word for one round, for labels: "day", "week", or "round". */
const roundNounFor = (type) =>
  type === 'daily' ? 'day' : type === 'weekly' ? 'week' : type === 'every_n' ? 'round' : null;

/** The assignments that pool one number, and can instead be scored per person. */
const POOLED_KINDS = ['crm_team', 'wing', 'role_total'];

/**
 * The individual role behind a pooled assignment.
 *
 * A CRM team is CROs, a wing is OGAs, and a role total already names its own
 * role. Anyone in the group who is NOT in that family drops out — the stored
 * participant list can only narrow a role, never add to it. That is the right
 * answer anyway: the quality auditors sitting in two Classroom teams and the
 * SOGOs in a wing record no payments at all. The form says how many are in.
 */
const individualRoleFor = (kind, ref) =>
  kind === 'role_total' ? ref : kind === 'wing' ? 'OGA' : 'CRO';

/** One value for the select, since an assignment is a (kind, ref) pair. */
const optionKey = (option) => `${option.kind}:${option.ref}`;

/**
 * Create / edit a challenge.
 *
 * A completed challenge opens read-only: after end_date it can be neither
 * edited nor deleted, and the API rejects it too — this is not the only guard.
 */
export default function ChallengeFormDialog({ challenge, isClone, open, setOpen, onSaved }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.challenges);

  // A clone starts from an existing challenge but creates a new one, so it is
  // never read-only — cloning a finished challenge to run it again is the main
  // reason the option exists.
  const isEdit = Boolean(challenge) && !isClone;
  const readOnly = !isClone && Boolean(challenge?.locked);
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState(null);
  /** Which input the message is about, so it can be outlined in red. */
  const [errorField, setErrorField] = useState(null);
  const faulty = (key) => errorField === key;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setErrorField(null);
    setValues(
      challenge
        ? {
            // Named so the copy is obvious in the list before it is edited.
            name: isClone ? `${challenge.name} (copy)` : challenge.name,
            type: challenge.type,
            scopeDeptId: challenge.scopeDeptId,
            assignmentType: challenge.assignmentType,
            assignmentRef: challenge.assignmentRef,
            startDate: challenge.startDate,
            endDate: challenge.endDate,
            participantUserIds: challenge.participantUserIds || [],
            bands: challenge.bands?.length
              ? challenge.bands
              : [{ from: '', to: '', tiers: [{ target: '', prize: '' }] }],
            partitionType: challenge.partitionType || 'none',
            partitionDays: challenge.partitionDays ?? '',
          }
        : EMPTY,
    );
  }, [open, challenge, isClone]);

  /**
   * The hand-picked people, which the list endpoint does not carry.
   *
   * Fetched separately rather than widening the list, which would cost a query
   * per row on a screen Finance reloads constantly for a field only the form
   * ever reads. A clone deliberately inherits the picks.
   */
  useEffect(() => {
    if (!open || !challenge?.id) return undefined;
    let cancelled = false;
    getChallengeById(challenge.id).then((res) => {
      if (cancelled || res?.status !== 'success') return;
      setValues((prev) => ({
        ...prev,
        participantUserIds: res.data.participant_user_ids || [],
        bands: (res.data.bands || []).map((b) => ({
          from: b.revenue_target_from ?? '',
          to: b.revenue_target_to ?? '',
          tiers: (b.tiers || []).map((t) => ({ target: t.target, prize: t.prize })),
        })),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [open, challenge?.id]);

  /**
   * Who a challenge can be set for, fetched per scope.
   *
   * Server-side rather than a constant: the options carry today's headcount and
   * the CRM teams that currently exist, both of which change as people move.
   */
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!open || !values.scopeDeptId) {
      setAssignmentOptions([]);
      return;
    }
    let cancelled = false;
    setLoadingOptions(true);
    getAssignmentOptions(values.scopeDeptId)
      .then((res) => {
        if (cancelled) return;
        setAssignmentOptions(res?.status === 'success' ? res.data : []);
      })
      .finally(() => !cancelled && setLoadingOptions(false));
    return () => {
      cancelled = true;
    };
  }, [open, values.scopeDeptId]);

  const set = (field) => (event) => {
    // Typing in the offending box clears its outline straight away.
    setErrorField(null);
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Assignment options are driven by scope, so a scope change has to clear the
  // assignment — otherwise you can save an OGD wing against a Classroom target.
  /**
   * Changing scope or assignment can take the prize ladder away — it is only
   * offered for OGD, and never for a head role. The flag has to go with it, or
   * the ladder stays switched on with nowhere to edit it and the single target
   * and prize stay hidden behind it, leaving a form with no figures at all.
   */
  const clearLadderIfUnavailable = (next) => {
    const canBand =
      Number(next.scopeDeptId) === OGD_DEPT_ID &&
      next.assignmentType === 'role' &&
      !HEAD_ROLES.includes(next.assignmentRef);
    // A flag left over from the previous assignment would silently turn a fresh
    // team challenge into a per-person one.
    if (!POOLED_KINDS.includes(next.assignmentType)) next.scoredIndividually = false;
    if (canBand) return next;
    // The ladder survives; only the revenue-target split has to go, collapsing
    // whatever groups were set into a single ladder for everyone.
    return {
      ...next,
      bands: [{ from: '', to: '', tiers: next.bands[0]?.tiers || [{ target: '', prize: '' }] }],
    };
  };

  const onScopeChange = (event) =>
    setValues((prev) =>
      clearLadderIfUnavailable({
        ...prev,
        scopeDeptId: event.target.value,
        assignmentType: '',
        assignmentRef: '',
      }),
    );

  const onAssignmentChange = (event) => {
    const [kind, ...rest] = event.target.value.split(':');
    setValues((prev) =>
      clearLadderIfUnavailable({
        ...prev,
        assignmentType: kind,
        assignmentRef: rest.join(':'),
      }),
    );
  };

  /** Roles that a per-person conversion can actually keep. */
  const familyRoles =
    values.assignmentType === 'wing'
      ? ['OGA']
      : values.assignmentType === 'role_total' && values.assignmentRef === 'OGA'
        ? ['OGA']
        : ['CRO', 'CRA', 'CAA', 'CSA'];

  /** The chosen assignment's own label, kept so the list still reads properly. */
  const assignmentLabel =
    assignmentOptions.find(
      (o) => o.kind === values.assignmentType && String(o.ref) === String(values.assignmentRef),
    )?.label || values.assignmentRef;

  const handleSubmit = (event) => {
    event.preventDefault();
    // A courtesy to whoever is typing. The API applies the same rules and is
    // what actually protects the data, so anything missed here still fails.
    const problem = validateForm(values);
    if (problem) {
      setError(problem.message);
      setErrorField(problem.field);
      return;
    }
    setError(null);
    setErrorField(null);

    // A per-person team challenge is SAVED as the individual role narrowed to
    // that team's members. The ids must be written out in full: an empty list
    // means "everyone in the role", which would be every CRO in the project.
    const chosen = picked.length ? picked : pickable.map((x) => x.user_id);
    const payload = perPerson
      ? {
          ...values,
          assignmentType: 'role',
          assignmentRef: individualRoleFor(values.assignmentType, values.assignmentRef),
          assignmentLabel: `${assignmentLabel} (each person)`,
          participantUserIds: chosen.filter((id) => pickable.some((x) => x.user_id === id)),
        }
      : values;

    const action = isEdit
      ? updateChallenge({ id: challenge.id, values: payload })
      : createChallenge(payload);

    dispatch(action).then((res) => {
      if (res.payload?.status !== 'success') {
        setError(res.payload?.message || 'Could not save the challenge');
        return;
      }
      enqueueSnackbar(isEdit ? 'Challenge updated' : 'Challenge created', { variant: 'success' });
      setOpen(false);
      dispatch(getChallenges());
      onSaved?.();
    });
  };

  const isAdmission = values.type === 'admission';

  // PM / CRM / SOGO / OGM record no payments themselves — their staff do — so a
  // challenge on one of these roles is scored on the team beneath them. Each
  // head still stands alone against the target; the number they are measured on
  // is their team's net total, so the figure typed here is a team-sized one.
  const isHeadRole =
    values.assignmentType === 'role' && HEAD_ROLES.includes(values.assignmentRef);

  // A team, a wing or a role total adds up to ONE number by default. Finance can
  // instead measure each member against the same ladder, which is how a project
  // gets what OGD gets from target groups: one challenge per CRM team, each with
  // its own targets. Stored as the individual role narrowed to those people, so
  // no new column — and if projects ever gain revenue targets, target groups
  // drop onto these challenges with nothing to undo.
  const isPooledKind = POOLED_KINDS.includes(values.assignmentType);
  const perPerson = isPooledKind && values.scoredIndividually;

  // A prize ladder — several targets, each with its own prize — is offered for
  // every assignment. Nothing about stacking rungs depends on who the people
  // are.
  const ladderAvailable = Boolean(values.assignmentRef);

  /**
   * SPLITTING that ladder by revenue target is narrower, because it needs the
   * targets to exist. 207 OGD people have ever carried one, against 3 in
   * Classroom and 2 in Foundation, none since last year — so outside OGD every
   * group would be empty and everybody would fall out of the challenge. Heads
   * are out too: their figure is their team's, which a personal target cannot
   * be banded against.
   */
  const canBandByTarget =
    Number(values.scopeDeptId) === OGD_DEPT_ID &&
    !isHeadRole &&
    // A wing or a team pools one number, and a pooled number cannot be matched
    // against a personal monthly target. Only a per-person challenge can split.
    values.assignmentType === 'role';

  // Whether the bands actually split by revenue target, or are one ladder for
  // everyone. A single band with no bounds is the "everyone" case.
  const bandedByTarget =
    canBandByTarget && values.bands.some((b) => b.from !== '' || b.to !== '');

  /**
   * The named heads behind the chosen assignment, for the picker.
   *
   * Only a head role gets one. Everyone else is measured individually already,
   * so there is nothing to narrow.
   */
  const [people, setPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);

  const needPeople = isHeadRole || perPerson;

  useEffect(() => {
    if (!open || !needPeople) {
      setPeople([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingPeople(true);
    getAssignmentPeople({
      scope: values.scopeDeptId,
      type: values.assignmentType,
      ref: values.assignmentRef,
    })
      .then((res) => {
        if (cancelled) return;
        setPeople(res?.status === 'success' ? res.data : []);
      })
      .finally(() => !cancelled && setLoadingPeople(false));
    return () => {
      cancelled = true;
    };
  }, [open, needPeople, values.scopeDeptId, values.assignmentType, values.assignmentRef]);

  // An empty selection means "everyone", so that is what All resets to.
  /**
   * Who the picker may actually offer.
   *
   * Scoring a team per person stores it as the individual role, and a stored
   * list can only narrow a role — so anyone outside that family cannot be kept
   * however hard you tick them. They are dropped from the picker rather than
   * offered and then silently discarded; the line above the picker names them.
   */
  const pickable = perPerson
    ? people.filter((x) => familyRoles.includes(x.role_label))
    : people;

  /**
   * The "select all" label.
   *
   * A head-role challenge is a list of heads, so "All CRMs" reads correctly.
   * A team's ref is the CRM's own user id, which read as "All 526s" until this
   * was split out.
   */
  const pickerAllLabel = isHeadRole
    ? `All ${values.assignmentRef}s`
    : values.assignmentType === 'wing'
      ? 'Everyone in this wing'
      : values.assignmentType === 'role_total'
        ? 'Everyone'
        : 'Everyone in this team';

  const picked = values.participantUserIds || [];
  const allPicked = picked.length === 0 || picked.length === pickable.length;

  const togglePerson = (userId) => {
    const current = picked.length ? picked : pickable.map((p) => p.user_id);
    const next = current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId];
    // Back to everyone: store nothing rather than every id, so people who join
    // the role later are still picked up.
    setValues((prev) => ({
      ...prev,
      participantUserIds: next.length === pickable.length ? [] : next,
    }));
  };

  const toggleAll = () => {
    setValues((prev) => ({
      ...prev,
      participantUserIds: allPicked ? pickable.map((p) => p.user_id).slice(0, -1) : [],
    }));
  };

  /**
   * Once a running challenge's first round has closed, that round has been
   * judged against these rounds. Re-cutting them would move days already
   * settled, so the period — and, when there are rounds, the start date — stay
   * as they were. The API refuses it too; this only says so before typing.
   */
  const roundsLocked =
    isEdit && !readOnly && Boolean(challenge?.startDate) && todayIst() > firstRoundEnd(challenge);
  const startLocked = roundsLocked && (challenge?.partitionType || 'none') !== 'none';

  const partitioned = values.partitionType && values.partitionType !== 'none';
  const roundSize = roundLength(values.partitionType, values.partitionDays);
  const roundNoun = partitioned ? roundNounFor(values.partitionType) : null;

  /** "10 days → 10 daily rounds", with a note when the last round is short. */
  const roundPreview = (() => {
    if (!partitioned || !roundSize || !values.startDate || !values.endDate) return null;
    if (values.endDate < values.startDate) return null;
    const days = daysBetween(values.startDate, values.endDate);
    const count = Math.ceil(days / roundSize);
    const short = days % roundSize;
    const kind =
      values.partitionType === 'daily'
        ? 'daily'
        : values.partitionType === 'weekly'
          ? 'weekly'
          : `${roundSize}-day`;
    return {
      text: `${days} day${days === 1 ? '' : 's'} → ${count} ${kind} round${count === 1 ? '' : 's'}`,
      note: short
        ? `The last round is ${short} day${short === 1 ? '' : 's'} long and is judged against the same ladder.`
        : null,
    };
  })();

  return (
    <GlobalDialog
      maxWidth="sm"
      fullWidth
      title={
        isClone
          ? `Clone of ${challenge.name}`
          : isEdit
            ? readOnly
              ? `${challenge.name} (completed)`
              : 'Edit challenge'
            : 'New challenge'
      }
      open={open}
      setOpen={setOpen}
      closeButton
      closeCondition={() => setOpen(false)}
      content={
        <form onSubmit={handleSubmit}>
          {readOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This challenge has ended, so it can no longer be edited or deleted.
            </Alert>
          )}
          {isClone && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Everything is copied from the original. Change the dates and whatever else
              differs — the original is not touched.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={values.name}
                error={faulty('name')}
                onChange={set('name')}
                disabled={readOnly || saving}
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Type"
                value={values.type}
                error={faulty('type')}
                onChange={set('type')}
                disabled={readOnly || saving}
              >
                {CHALLENGE_TYPES.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Scope"
                value={values.scopeDeptId}
                error={faulty('scopeDeptId')}
                onChange={onScopeChange}
                disabled={readOnly || saving}
              >
                {CHALLENGE_SCOPES.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Assignment"
                value={values.assignmentRef ? `${values.assignmentType}:${values.assignmentRef}` : ''}
                error={faulty('assignmentRef')}
                onChange={onAssignmentChange}
                disabled={readOnly || saving || !values.scopeDeptId || loadingOptions}
                helperText={
                  !values.scopeDeptId
                    ? 'Pick a scope first.'
                    : loadingOptions
                      ? 'Loading…'
                      : 'Always a role or a group — never a named person.'
                }
              >
                {assignmentOptions.map((option) => (
                  <MenuItem key={optionKey(option)} value={optionKey(option)}>
                    <ListItemText
                      primary={option.label}
                      secondary={`${option.participants} ${
                        option.participants === 1 ? 'person' : 'people'
                      } today`}
                    />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* A team, a wing and a role total all add up to one number by
                default. Scoring each member separately is how a project gets
                what OGD gets from target groups: one challenge per CRM team,
                each team with its own targets. */}
            {isPooledKind && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    How is it scored?
                  </Typography>
                  <RadioGroup
                    row
                    value={values.scoredIndividually ? 'each' : 'total'}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        scoredIndividually: e.target.value === 'each',
                        participantUserIds: [],
                      }))
                    }
                  >
                    <FormControlLabel
                      value="total"
                      disabled={readOnly || saving}
                      control={<Radio size="small" />}
                      label="One team total"
                    />
                    <FormControlLabel
                      value="each"
                      disabled={readOnly || saving}
                      control={<Radio size="small" />}
                      label="Each person"
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary">
                    {values.scoredIndividually
                      ? 'Each person is measured on their own, against the same ladder.'
                      : 'Everyone adds up to one number, measured against one ladder.'}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* A head records no payments themselves, so the challenge is really
                about which heads' teams are in it. Default is all of them; untick
                to run it for a few. Leaving it on All keeps picking up people who
                take the role later, which storing every id would not. */}
            {needPeople && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allPicked}
                          indeterminate={!allPicked && picked.length > 0}
                          onChange={toggleAll}
                          disabled={readOnly || saving || loadingPeople || pickable.length === 0}
                        />
                      }
                      label={<Typography variant="subtitle2">{pickerAllLabel}</Typography>}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {loadingPeople
                        ? 'Loading…'
                        : `${allPicked ? pickable.length : picked.length} of ${pickable.length} selected`}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ maxHeight: 190, overflowY: 'auto' }}>
                    {pickable.map((person) => (
                      <FormControlLabel
                        key={person.user_id}
                        sx={{ display: 'flex', ml: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={allPicked || picked.includes(person.user_id)}
                            onChange={() => togglePerson(person.user_id)}
                            disabled={readOnly || saving}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {person.user_name}
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, color: 'text.secondary' }}
                            >
                              {person.role_label}
                            </Typography>
                          </Typography>
                        }
                      />
                    ))}
                    {!loadingPeople && people.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                        Nobody currently holds this role.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}

            {ladderAvailable && (
              <Grid item xs={12}>
                {/* No switch: a challenge IS its ladder now. One target and one
                    prize is simply a ladder with a single row, so there is
                    nothing to turn on. */}
                <BandLadderBuilder
                  canBandByTarget={canBandByTarget}
                  errorField={errorField}
                  usesBands={bandedByTarget}
                  bands={values.bands}
                  isAdmission={isAdmission}
                  roundNoun={roundNoun}
                  disabled={readOnly || saving}
                  onChange={(bands) => {
                    setErrorField(null);
                    setValues((prev) => ({ ...prev, bands }));
                  }}
                  onToggleBands={(on) =>
                    setValues((prev) => ({
                      ...prev,
                      bands: on
                        ? prev.bands.map((b, i) => (i === 0 ? { ...b, from: 100000 } : b))
                        : [{ ...prev.bands[0], from: '', to: '' }],
                    }))
                  }
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <DatePickerCustom
                label="Start date"
                fullWidth
                value={values.startDate ? dayjs(values.startDate) : null}
                setValue={(date) =>
                  setValues((prev) => ({ ...prev, startDate: date ? date.format('YYYY-MM-DD') : '' }))
                }
                disabled={readOnly || saving || startLocked}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePickerCustom
                label="End date"
                fullWidth
                value={values.endDate ? dayjs(values.endDate) : null}
                setValue={(date) =>
                  setValues((prev) => ({ ...prev, endDate: date ? date.format('YYYY-MM-DD') : '' }))
                }
                disabled={readOnly || saving}
              />
            </Grid>

            {/* Rounds. Whole duration is the original behaviour: one total,
                one ladder. Anything else re-runs the same ladder each round
                and adds the prizes up. */}
            <Grid item xs={values.partitionType === 'every_n' ? 8 : 12}>
              <TextField
                select
                fullWidth
                label="Prize period"
                value={values.partitionType}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    partitionType: e.target.value,
                    partitionDays: e.target.value === 'every_n' ? prev.partitionDays || 3 : '',
                  }))
                }
                disabled={readOnly || saving || roundsLocked}
                helperText={
                  roundsLocked
                    ? 'Locked — the first round has already closed.'
                    : roundPreview
                      ? [roundPreview.text, roundPreview.note].filter(Boolean).join('. ')
                      : null
                }
              >
                {PARTITION_TYPES.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {values.partitionType === 'every_n' && (
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Days"
                  value={values.partitionDays}
                  error={faulty('partitionDays')}
                  onChange={set('partitionDays')}
                  inputProps={{ min: 2, max: MAX_PARTITION_DAYS }}
                  disabled={readOnly || saving || roundsLocked}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Dates are inclusive and read in IST. Everyone who reaches the target gets the
                prize — a challenge is not a competition.
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Stack direction="row" spacing={1}>
              <CancelButton action={() => setOpen(false)}>
                {readOnly ? 'Close' : 'Cancel'}
              </CancelButton>
              {!readOnly && (
                <SubmitButton type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isEdit ? 'Save changes' : isClone ? 'Create copy' : 'Create'}
                </SubmitButton>
              )}
            </Stack>
          </Box>
        </form>
      }
    />
  );
}

ChallengeFormDialog.propTypes = {
  /** Absent for create; the source challenge for edit and for clone. */
  challenge: PropTypes.object,
  /** Prefill from `challenge` but create a new one rather than editing it. */
  isClone: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
